<template>
  <div v-if="!loading" class="flex flex-column items-center col-12 relative full-height">
    <div class="flex flex-wrap col-12 ">
      <span class="flex col-12 justify-center items-end h3n bold mt4 caps">
        Merci pour votre commande!
      </span>
      <hr class="flex flex-auto col-12">
    </div>

    <div v-if="isImpersonateMode && order && order.order && order.order.payment_link_url" class="col-12 h5n flex justify-center center mt1 mb3">
      Un <a :href="order.order.payment_link_url" class="mx1 underline"><span>lien de paiement</span></a> a été envoyé par email à l’adresse {{ user.email }}
    </div>

    <div class="flex flex-wrap col-12 full-height">
      <div class="flex col-12 flex-column items-start pt3">
        <div class="flex center col-12 justify-center h6 my2">Merci d'avoir choisi l'UCPA ! Vous retrouverez les informations de votre commande et de votre paiement dans l'email de confirmation.</div>
        <div v-if="isOrderFromFormations" class="flex center col-12 justify-center h6 my2">Votre commande est accessible depuis votre espace personnel.</div>
        <div v-else class="flex center col-12 justify-center h6 my2">Le jour de votre séance, présentez votre e-billet pour accéder à votre espace de pratique. L'e-billet est accessible depuis votre espace personnel dans la rubrique "Mon compte / Mes e-billets".</div>

        <div class="flex col-12 justify-center items-center mt4 mb2 caps">
          <Button :action="goToHomepage" text="mon espace personnel" class="flex items-center col-5 sm-col-12" />
        </div>
      </div>

      <div class="col-12 h6 flex flex-wrap justify-center items-end center mb4">
        <div class="col-12 justify-center items-center center">Si vous constatez une erreur, veuillez prendre contact en vous rendant sur la page : <a class="underline" :href="accessUrl">{{ accessUrl }}</a></div>
      </div>
    </div>
  </div>
</template>

<script>
import Cookies from 'js-cookie';
import Button from '../../components/elements/Button';

import { computeProductArrayByCart, createSha256Hash } from '@/utils/index';

import { getOrderByUuid, getProduct } from '@/utils/requests';
import { changeToPage, handleOfferInfo } from '@/utils/logics';

/* Set by CourtSessionUnpaidOrder.vue before opening the Sharegroop regularisation tab, since that
 * tab (not the original one) is the one Sharegroop redirects back here on success. */
const UNPAID_REDIRECT_COOKIE = 'unpaid_redirect_target';

export default {
  components: { Button },
  data () {
    return {
      workspace: this.$store.getters.workspace,
      cart: this.$store.state.cart,
      site: this.$store.state.siteInfo,
      parceled: false
    };
  },
  computed: {
    user () {
      return this.$store.state.user;
    },
    loading () {
      return this.$store.state.loading;
    },
    order () {
      return this.$store.state.order;
    },
    isOrderFromFormations () {
      return this.order && this.order.site_workspace === 'alpha_formations';
    },
    priceInfo () {
      return this.cart.items ? this.cart.items.find(item => item.workspace === this.workspace).priceInfo : null;
    },
    totalPriceCart () {
      return this.priceInfo ? this.priceInfo.totalPrice : null;
    },
    value () {
      return this.totalPriceCart > 0 ? `${this.totalPriceCart} €` : 'Offert';
    },
    accessUrl () {
      if (this.site.entry_site) {
        return `${process.env.VUE_APP_UCPA_DOMAIN}/${this.site.site_preroute}/acces-contacts`;
      }
      return `${process.env.VUE_APP_UCPA_DOMAIN}/${this.site.site_preroute}/${this.site.site_url}/acces-contacts`;
    },
    isImpersonateMode () {
      return this.$store.getters.isImpersonateMode;
    }
  },
  created () {
    this.$store.commit('setLoading', true);
    this.getOrder();
  },
  methods: {
    async handleProduct (product, offerCode) {
      const responseProduct = await getProduct({ product });
      return handleOfferInfo(responseProduct, offerCode);
    },
    goToHomepage () {
      const baseUrl = `${process.env.VUE_APP_UCPA_DOMAIN}/${this.site.site_preroute}/espacepersonnel/${this.site.site_url}`;
      const impersonateToken = this.$store.state.impersonateData;

      if (impersonateToken) {
        window.location.href = `${baseUrl}?impersonateData=${impersonateToken}`;
        return;
      }

      window.location.href = baseUrl;
    },
    async getOrder () {
      const { order, isCourtSession } = this.$route.query;

      await getOrderByUuid(order).then(async response => {
        const { order: orderPayload } = response;

        if (orderPayload) {
          this.$store.commit('setOrder', orderPayload);

          const isSubscriptionOrder = orderPayload.order.order_items.some(oi => oi.recurring);
          const isUnpaidRegularisation = Cookies.get(UNPAID_REDIRECT_COOKIE) === 'court';

          if ((isCourtSession === 'true' && orderPayload.order.isCourtSession) || (isSubscriptionOrder && isUnpaidRegularisation)) {
            Cookies.remove(UNPAID_REDIRECT_COOKIE);
            /* subscriptionTDA drives the StepBar visibility; it must be cleared or the abandoned subscription steps show up on the court screens. */
            this.$store.commit('setSubscriptionTDA', false);
            await this.handleProduct(orderPayload.order.order_items[0].product_uuid, orderPayload.order.order_items[0].horanet_product_code);
            changeToPage('CourtReservation');
            return;
          }

          this.parceled = orderPayload.order.installment > 0;

          // get product
          const { product, newTDA, subscriptionTDA } = this.$store.state;

          try {
            // data layer
            const orderDataLayer = {
              pagePath: '/confirmation',
              checkoutStepName: 'confirmation',
              customerEmailAddressSHA256: createSha256Hash(this.user.email),
              customerPhoneNumberSHA256: createSha256Hash(this.user.mobilephone),
              event: 'ecommerceNEO',
              eventCommand: 'orderConfirmation',
              orderPaymentMethod: 'card',
              orderID: orderPayload.uuid,
              orderRevenue: parseFloat(this.totalPriceCart) || this.totalPriceCart,
              orderTax: 0,
              orderShipping: 0,
              tunnelType: (() => {
                if (!newTDA) return 'tunnel classique';
                if (subscriptionTDA) return 'tunnel abonnement';
                return 'tunnel entrées unitaires';
              })(),
              orderCoupon: undefined,
              productArray: []
            };

            orderPayload.order.order_items.map(order_item => {
              // get product offer that was bought
              const dataLayer = computeProductArrayByCart(this.$store.state.cartItem, product, this.$store.state.offerInfo, this.$store.state.siteInfo);
              orderDataLayer.productArray = dataLayer.productArray;

              return order_item;
            });

            window.dataLayer.push({ ...orderDataLayer });
          } catch (error) {
            console.log('Error populating datalayer', error);
          }
        }
      });

      this.$store.commit('setLoading', false);
      // TODO
    }
  }
};
</script>
